/**
 * Floor captain + coverage dispatch (headless, pure — no PixiJS).
 *
 * Mirrors the single-holder duty pattern of `updateBlackoutWitness`: exactly one
 * live guard is the captain; it roams between room centres and, when a room's
 * guard roster changes (a death, or a debug spawn), the room is re-partitioned
 * across its survivors so the whole room stays covered — the "cover for a
 * missing guard" behaviour. Called once per tick after `updateBlackoutWitness`
 * and before the movement pass, so the assignments it writes are executed the
 * same tick by `updateGuard`.
 */

import { CONFIG } from '../config';
import type { Guard } from './guard';
import type { MapData } from './map';
import { makeCaptain, repartitionRoom, type FloorPatrol } from './patrol';

/** Tuning shape consumed here — matches `CONFIG.guard`. */
export interface DispatchCfg {
  captain: { dwellTicks: number };
}

/** What the captain did this tick, for the caller to log. */
export interface DispatchResult {
  reassigned: number; // guards moved between rooms
  rested: number; // guards newly sent to the green room
  recalled: number; // reserves recalled to a threat
}

export function updateDispatch(guards: Guard[], map: MapData, floor: FloorPatrol, cfg: DispatchCfg): DispatchResult {
  const empty: DispatchResult = { reassigned: 0, rested: 0, recalled: 0 };
  // 1. Captain election / reselect (single holder).
  let captain = guards.find((g) => g.isCaptain && !g.body.dead) ?? null;
  if (!captain) captain = electCaptain(guards, floor);
  if (!captain) return empty; // no live guards to command

  // 2. Threat recall FIRST (so recalled reserves are already agitated when the
  //    reallocation runs and won't be re-rested this tick).
  const recalled = recallReserves(guards);
  // 3. Reallocate: even out the shortage across rooms, retire surplus to rest.
  const { reassigned, rested } = reallocateGuards(guards, floor, map);
  if (reassigned + rested + recalled > 0) captain.alertingTimer = Math.max(captain.alertingTimer, 1.5);
  // 4. Captain roam between room centres, pausing at each.
  updateCaptainRoam(captain, floor, cfg);
  return { reassigned, rested, recalled };
}

/**
 * Balance the floor's guards against each room's quota (`expectedCount`).
 * Under-staffed → EVEN OUT the shortage (greedy: fill the largest deficit); over
 * total capacity → the extra guards retire to the green room to rest. Only moves
 * homeRoomId (harmless while a guard investigates) and only rests UNAWARE guards
 * (never yanks one off an active investigation).
 */
function reallocateGuards(guards: Guard[], floor: FloorPatrol, map: MapData): { reassigned: number; rested: number } {
  const rooms = floor.rooms;
  if (rooms.length === 0) return { reassigned: 0, rested: 0 };
  const capacity = rooms.reduce((s, r) => s + r.expectedCount, 0);
  const pool = guards.filter((g) => !g.body.dead && !g.isCaptain);

  // Even-out target counts: hand each of the first `min(pool, capacity)` guards
  // to the room with the largest RELATIVE deficit (`deficit / quota`), so the
  // shortage spreads proportionally rather than emptying the small rooms.
  // Compared via cross-multiplication (exact); ties → the larger quota.
  const target = new Map<number, number>();
  for (const r of rooms) target.set(r.roomId, 0);
  const n = Math.min(pool.length, capacity);
  for (let i = 0; i < n; i++) {
    let best: (typeof rooms)[number] | null = null;
    let bestDef = 0;
    let bestQuota = 1;
    for (const r of rooms) {
      const def = r.expectedCount - target.get(r.roomId)!;
      if (def <= 0) continue; // room already full
      // def/quota > bestDef/bestQuota  ⇔  def*bestQuota > bestDef*quota
      const lhs = def * bestQuota;
      const rhs = bestDef * r.expectedCount;
      if (best === null || lhs > rhs || (lhs === rhs && r.expectedCount > bestQuota)) {
        best = r; bestDef = def; bestQuota = r.expectedCount;
      }
    }
    if (!best) break;
    target.set(best.roomId, target.get(best.roomId)! + 1);
  }

  // Assign guards to rooms — keep each in its current room where a slot remains.
  const assigned = new Map<number, Guard[]>();
  for (const r of rooms) assigned.set(r.roomId, []);
  const leftover: Guard[] = [];
  for (const g of pool) {
    const rid = g.homeRoomId;
    if (rid !== null && !g.resting && assigned.has(rid) && assigned.get(rid)!.length < target.get(rid)!) {
      assigned.get(rid)!.push(g);
    } else {
      leftover.push(g);
    }
  }
  for (const r of rooms) {
    let need = target.get(r.roomId)! - assigned.get(r.roomId)!.length;
    while (need > 0 && leftover.length > 0) {
      let bi = 0;
      let bd = Infinity;
      for (let j = 0; j < leftover.length; j++) {
        const d = (leftover[j]!.x - r.centerX) ** 2 + (leftover[j]!.y - r.centerY) ** 2;
        if (d < bd) { bd = d; bi = j; }
      }
      assigned.get(r.roomId)!.push(leftover.splice(bi, 1)[0]!);
      need--;
    }
  }
  // `leftover` now = guards beyond capacity → rest (unaware ones only).

  let reassigned = 0;
  let rested = 0;
  for (const r of rooms) {
    const a = assigned.get(r.roomId)!;
    // Repartition when the section count is stale (a death/addition changed the
    // roster size) or any assigned guard isn't already homed here (a move / a
    // recalled rester).
    const changed = a.length !== r.assignedCount || a.some((g) => g.homeRoomId !== r.roomId || g.resting);
    if (!changed) continue;
    for (const g of a) { if (g.homeRoomId !== r.roomId || g.resting) reassigned++; g.resting = false; }
    repartitionRoom(r, a, map);
  }
  const greenRoom = floor.greenRoom;
  for (const g of leftover) {
    if (g.resting || g.state !== 'unaware') continue; // don't rest an active guard
    g.resting = true;
    g.homeRoomId = null;
    g.sectionId = null;
    g.secX0 = null; g.secY0 = null; g.secX1 = null; g.secY1 = null;
    g.secAnchorX = null; g.secAnchorY = null;
    g.scanning = false;
    g.path = null;
    if (greenRoom) { g.coverTargetX = greenRoom.centerX; g.coverTargetY = greenRoom.centerY; }
    rested++;
  }
  return { reassigned, rested };
}

/** If a threat is active (any live guard cautious/alert with a target), recall
 *  every resting reserve to investigate the reported spot. */
function recallReserves(guards: Guard[]): number {
  const threat = guards.find(
    (g) => !g.body.dead && (g.state === 'cautious' || g.state === 'alert') && g.investigationX !== null,
  );
  if (!threat) return 0;
  const cautious = CONFIG.awareness.thresholds.cautious;
  let recalled = 0;
  for (const g of guards) {
    if (!g.resting || g.body.dead) continue;
    g.resting = false;
    if (g.awareness < cautious) g.awareness = cautious;
    g.state = 'cautious';
    g.investigationX = threat.investigationX;
    g.investigationY = threat.investigationY;
    g.lastKnownPlayerX = threat.lastKnownPlayerX;
    g.lastKnownPlayerY = threat.lastKnownPlayerY;
    g.path = null;
    recalled++;
  }
  return recalled;
}

/** Promote the live guard nearest the floor centroid to captain (id tiebreak). */
function electCaptain(guards: Guard[], floor: FloorPatrol): Guard | null {
  const live = guards.filter((g) => !g.body.dead).sort((a, b) => a.id - b.id);
  if (live.length === 0) { floor.captainId = null; return null; }
  const n = Math.max(1, floor.rooms.length);
  let ax = 0;
  let ay = 0;
  for (const rp of floor.rooms) { ax += rp.centerX; ay += rp.centerY; }
  ax /= n;
  ay /= n;
  let best: Guard | null = null;
  let bestD = Infinity;
  for (const g of live) {
    const d = (g.x - ax) ** 2 + (g.y - ay) ** 2;
    if (d < bestD) { bestD = d; best = g; }
  }
  if (!best) return null;
  const rc = nearestRoomCenter(best, floor);
  makeCaptain(best, rc?.x ?? best.x, rc?.y ?? best.y);
  floor.captainId = best.id;
  return best;
}

/**
 * Every patrol region (section) on the floor, in room→section order, as the
 * captain's roam route. He walks each one to check on it; a room with no live
 * sections falls back to its centre so an empty room still gets visited. Rebuilt
 * each tick (sections re-partition on deaths), so `captainWaypointIdx` is always
 * taken modulo the current length.
 */
function captainWaypoints(floor: FloorPatrol): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  for (const rp of floor.rooms) {
    if (rp.sections.length === 0) {
      out.push({ x: rp.centerX, y: rp.centerY });
    } else {
      for (const s of rp.sections) out.push({ x: s.anchorX, y: s.anchorY });
    }
  }
  return out;
}

/** Advance the captain's round-robin roam target through every patrol region. */
function updateCaptainRoam(captain: Guard, floor: FloorPatrol, cfg: DispatchCfg): void {
  const wps = captainWaypoints(floor);
  if (wps.length === 0) return;
  if (captain.coverTargetX === null || captain.coverTargetY === null) {
    const wp = wps[floor.captainWaypointIdx % wps.length]!;
    captain.coverTargetX = wp.x;
    captain.coverTargetY = wp.y;
    return;
  }
  const atTarget = captain.coverTargetX === captain.x && captain.coverTargetY === captain.y;
  if (!atTarget) return;
  captain.roamDwellTicks += 1;
  if (captain.roamDwellTicks < cfg.captain.dwellTicks) return;
  floor.captainWaypointIdx = (floor.captainWaypointIdx + 1) % wps.length;
  const next = wps[floor.captainWaypointIdx]!;
  captain.coverTargetX = next.x;
  captain.coverTargetY = next.y;
  captain.roamDwellTicks = 0;
  captain.path = null;
}

function nearestRoomCenter(guard: Guard, floor: FloorPatrol): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (const rp of floor.rooms) {
    const d = (rp.centerX - guard.x) ** 2 + (rp.centerY - guard.y) ** 2;
    if (d < bestD) { bestD = d; best = { x: rp.centerX, y: rp.centerY }; }
  }
  return best;
}
